import Constants from "expo-constants";
import React, { use, useCallback, useEffect, useMemo } from "react";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { PickerOptionsMode, useDocumentUpload } from "../../hooks/documents";
import { StackScreenProps } from "@react-navigation/stack";
import VStack from "../../ui_atoms/VStack";
import Gap from "../../ui_atoms/Gap";
import { useI18n, useNationalityFormatter } from "../../hooks/i18n-hooks";
import {
    ClientStatus,
    Document,
    DocumentType,
    ProfileType,
    RejectedByType,
} from "../../__generated__/graphql";
import { DisplayableDocument } from "../../contexts/DocumentManager";
import { useIntercom } from "../../hooks/intercom";
import { useMutation } from "@apollo/client/react";
import { UPDATE_USER } from "../../graphql/identity";
import ListItem, { buildDescription } from "../../ui_atoms/ListItem";
import SimplePageContent from "../../components/SimplePageContent";
import ListTitle from "../../ui_atoms/ListTitle";
import Loading from "../../components/Loading";
import { UPDATE_CLIENT_PROFILE } from "../../graphql/core";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { useTheme } from "../../hooks/use-theme";
import { useWorkerDocumentManager } from "../../hooks/user-worker-document";
import DocumentEditorModal from "../../components/document/DocumentEditorModal";
import { HeaderButton } from "./ScreenHeader";
import { useDeleteAccount } from "../../hooks/use-delete-account";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useGetIsWorkerProfileComplete } from "../../hooks/use-get-is-worker-profile-complete";
import { useDeleteCurrentProfile } from "../../hooks/use-delete-current-profile";
import AppVersion from "../../components/AppVersion";
import EmployerProfileWarnings from "../../components/EmployerProfileWarnings";
import { useBusinessEmailVerificationHeadband } from "../../hooks/use-business-email-verification-headband";
import { ProfileStackParamList } from "../../navigations/ProfileStack";
import { useDateFormatter } from "../../hooks/date";
import { FirebaseManagerContext } from "../../contexts/FirebaseManager";
import { openStorybook } from "../../storybook";
import { useSetAlert } from "../../hooks/use-set-alert";
import Label, { LabelProps } from "../../ui_atoms/Label";
import { escapeMarkdown, SYSTEM_NAME } from "../../utils";
import LanguageSwitcher from "../../components/LanguageSwitcher";

type ProfileToUse = "Worker" | "Client" | "PrivateClient" | "Unknown";

type ProfileScreenProps = StackScreenProps<RootStackParamList & ProfileStackParamList>;

const ProfileScreen = ({ navigation }: ProfileScreenProps) => {
    const i18n = useI18n();
    const { colors, gridSize } = useTheme();
    const { setAlert } = useSetAlert();

    const [devClickCount, setDevClickCount] = React.useState(0);
    const [updatingAvatar, setUpdatingAvatar] = React.useState(false);
    const [updatingLogo, setUpdatingLogo] = React.useState(false);
    const { signOut } = use(FirebaseManagerContext);
    const { user, refreshUser } = use(AuthManagerContext);
    const { profileType, openProfileSwitch } = use(ProfileSwitcherManagerContext);
    const currentProfile = useCurrentProfile();
    const [updateUser, { error: saveAvatarError, reset: saveAvatarReset }] =
        useMutation(UPDATE_USER);
    const [updateClientProfile, { error: saveLogoError, reset: saveLogoReset }] =
        useMutation(UPDATE_CLIENT_PROFILE);

    const onDeleteAccount = useDeleteAccount();
    const onDeleteWorkerProfile = useDeleteCurrentProfile({ profileType: ProfileType.Worker });
    const onDeleteClientProfile = useDeleteCurrentProfile({ profileType: ProfileType.Client });
    const onDeletePrivateClientProfile = useDeleteCurrentProfile({
        profileType: ProfileType.Client,
        isPrivateClient: true,
    });
    const getIsWorkerProfileComplete = useGetIsWorkerProfileComplete();
    const { isValid: isWorkerProfileComplete } = getIsWorkerProfileComplete(user);

    const client = currentProfile?.client;
    const isPrivateClient = currentProfile?.isPrivateClient || false;
    const emailVerificationHeadband = useBusinessEmailVerificationHeadband();

    const { removeWorkerDocument } = useWorkerDocumentManager();

    const [displayedDocument, setDisplayedDocument] = React.useState<
        DisplayableDocument | undefined
    >(undefined);

    const uploadNewProfile = React.useCallback<Parameters<typeof useDocumentUpload>[0]["onSelect"]>(
        async ({ documentUrl }) => {
            await updateUser({
                variables: { userData: { avatarUrl: documentUrl } },
            });
            // this will force to update user and so refresh avatar
            await refreshUser?.();
        },
        [refreshUser, updateUser],
    );

    const uploadNewLogo = React.useCallback<Parameters<typeof useDocumentUpload>[0]["onSelect"]>(
        async ({ documentUrl }) => {
            try {
                await updateClientProfile({
                    variables: { changes: { client: { logoUrl: documentUrl } } },
                });
                // this will force to update user and so refresh avatar
                await refreshUser?.();
            } finally {
                /* empty */
            }
        },
        [updateClientProfile, refreshUser],
    );

    const { documentDropdownProps: profilePicturePickerProps } = useDocumentUpload({
        documentType: DocumentType.ProfilePicture,
        isPublic: true,
        modes: [PickerOptionsMode.Library, PickerOptionsMode.Camera, PickerOptionsMode.Web],
        setIsLoading: setUpdatingAvatar,
        onSelect: uploadNewProfile,
        webUploadFn: "profilePicture",
        enableCropZoom: true,
        storageTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
    });

    const { documentDropdownProps: companyPicturePickerProps } = useDocumentUpload({
        documentType: DocumentType.CompanyPicture,
        generateUniqueId: true,
        isPublic: true,
        modes: [PickerOptionsMode.Library, PickerOptionsMode.Camera, PickerOptionsMode.Web],
        setIsLoading: setUpdatingLogo,
        onSelect: uploadNewLogo,
        webUploadFn: "companyLogo",
        enableCropZoom: true,
        storageTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
    });

    const editWorkerProfile = useCallback(() => {
        navigation.navigate("WorkerProfileEdit");
    }, [navigation]);

    const editClientProfile = useCallback(() => {
        navigation.navigate("ClientProfileEdit");
    }, [navigation]);

    const editAvsNumber = useCallback(() => {
        navigation.navigate("AvsNumberEdit");
    }, [navigation]);

    const editAccount = useCallback(() => {
        // All profiles edit their account email through the verification-enabled EmailEditor so it
        // can be changed and re-verified from here. (AccountEdit stays reachable via its deep link.)
        navigation.navigate("EmailEdit");
    }, [navigation]);

    const editBusinessEmail = useCallback(() => {
        navigation.navigate("EmailEdit", { businessEmail: true });
    }, [navigation]);

    const editPhoneNumber = useCallback(() => {
        navigation.navigate("PhoneNumberEdit");
    }, [navigation]);

    const editClient = useCallback(() => {
        navigation.navigate("ClientEdit");
    }, [navigation]);

    const editClientContact = useCallback(() => {
        navigation.navigate("ClientContactEdit");
    }, [navigation]);

    const editBankAccount = useCallback(() => {
        navigation.navigate("BankAccountEdit");
    }, [navigation]);

    const editDiplomaAndTraining = useCallback(() => {
        navigation.navigate("ExperiencesEdit");
    }, [navigation]);

    const toActivityLog = useCallback(() => {
        navigation.navigate("UserLogs");
    }, [navigation]);

    const toColleagues = useCallback(() => {
        navigation.navigate("Colleagues");
    }, [navigation]);

    const toFrameworkContract = useCallback(() => {
        if (client?.id && client?.contractEffectiveAsset && client?.signatureStatus) {
            if (client.status === ClientStatus.Rejected) {
                if (client.contractEffectiveAsset.url) {
                    navigation.navigate("PdfViewer", {
                        title: i18n.t("profile.framework_contract"),
                        assetUrl: client.contractEffectiveAsset.url,
                        topMarkdown: {
                            text: i18n.t("profile.rejected_framework_contract", {
                                rejectedBy: escapeMarkdown(
                                    client.rejectedBy === RejectedByType.User
                                        ? client.name
                                        : SYSTEM_NAME,
                                ),
                                reason: escapeMarkdown(client.rejectedReason),
                            }),
                            color: colors.error,
                        },
                    });
                }
            } else {
                navigation.navigate("ContractPreviewScreen", {
                    contractType: "client_framework",
                    contractId: client.id,
                });
            }
        }
    }, [
        client?.contractEffectiveAsset,
        client?.id,
        client?.name,
        client?.rejectedBy,
        client?.rejectedReason,
        client?.signatureStatus,
        client?.status,
        colors.error,
        i18n,
        navigation,
    ]);

    const onLogout = useCallback(() => {
        setAlert({
            title: i18n.t("profile.logout_title"),
            message: i18n.t("profile.logout_description"),
            buttons: [
                {
                    text: i18n.t("profile.logout_confirm"),
                    onPress: () => {
                        void signOut();
                    },
                    style: "destructive",
                },
                {
                    text: i18n.t("cancel"),
                    style: "cancel",
                },
            ],
            options: { cancelable: true },
        });
    }, [i18n, setAlert, signOut]);

    const isWorkerProfile = profileType === ProfileType.Worker;
    const isClientProfile = profileType === ProfileType.Client;
    const { presentIntercom } = useIntercom();
    const intlFormat = useDateFormatter();
    const civilState = useMemo(() => {
        if (!user || !isWorkerProfile) {
            return undefined;
        }
        return user.workerProfile?.maritalStatusFrom
            ? i18n.t("profile.civil_state", {
                  state: i18n.t(`user.${user.workerProfile?.maritalStatus || "single"}`),
                  date: intlFormat(new Date(user.workerProfile.maritalStatusFrom || "")),
              })
            : i18n.t("profile.civil_state_simple", {
                  state: i18n.t(`user.${user.workerProfile?.maritalStatus || "single"}`),
              });
    }, [user, isWorkerProfile, i18n, intlFormat]);

    const formatNationality = useNationalityFormatter();

    useApolloErrorSnackbar(saveAvatarError, saveAvatarReset, i18n.t("profile.save_avatar_error"));
    useApolloErrorSnackbar(saveLogoError, saveLogoReset, i18n.t("profile.save_logo_error"));

    useEffect(() => {
        navigation?.setOptions({
            headerRight: () => (
                <HeaderButton
                    onPress={() => void presentIntercom()}
                    label={i18n.t("assistance")}
                    style={{
                        alignItems: "center",
                        alignSelf: "flex-end",
                        marginRight: gridSize / 2,
                    }}
                />
            ),
        });
    }, [colors.secondary, gridSize, i18n, navigation, presentIntercom]);

    const onThrowError = useCallback(() => {
        throw new Error("I told you not to click!");
    }, []);

    const acceptedClients = useMemo(
        () =>
            (user?.clientProfiles || []).filter((clientProfile) =>
                [ClientStatus.Accepted, ClientStatus.Signed].includes(clientProfile.client.status),
            )?.length || 0,
        [user?.clientProfiles],
    );

    const validProfilesNb = useMemo(
        () => acceptedClients + (isWorkerProfileComplete ? 1 : 0),
        [acceptedClients, isWorkerProfileComplete],
    );

    const ijmGroup = client?.ijmGroup;
    const canChangeIjmGroup = client?.canChangeIjmGroup || false;
    const profileToUse = useMemo<ProfileToUse>(() => {
        if (isWorkerProfile) {
            return "Worker";
        }
        if (isClientProfile) {
            if (isPrivateClient) {
                return "PrivateClient";
            }
            return "Client";
        }
        return "Unknown";
    }, [isClientProfile, isPrivateClient, isWorkerProfile]);

    const baseComponents = useMemo(() => {
        return (
            <DocumentEditorModal
                visible={!!displayedDocument}
                document={displayedDocument}
                onClose={() => setDisplayedDocument(undefined)}
                onRemove={() => {
                    if (displayedDocument) {
                        void removeWorkerDocument(displayedDocument);
                        setDisplayedDocument(undefined);
                    }
                }}
            />
        );
    }, [displayedDocument, removeWorkerDocument]);

    const personalInfo = useMemo(() => {
        if (!user) {
            return null;
        }
        return (
            <>
                <ListTitle value={i18n.t("profile.personal_info")} />
                <ListItem
                    {...profilePicturePickerProps}
                    avatarPath={user.avatarUrl}
                    isAvatarLoading={updatingAvatar}
                    labels={[{ text: i18n.t("profile.change_profile_picture"), mode: "cta" }]}
                    rightActionMode="empty"
                    useBigAvatarOrIcon={true}
                />
                <ListItem
                    icon="user"
                    description={buildDescription([
                        i18n.t(`profile.title.${user.gender || "unknown"}`, {
                            firstName: user.firstName,
                            lastName: user.lastName,
                        }),
                        user.address?.formatted,
                        user.birthdate ? intlFormat(new Date(user.birthdate)) : undefined,
                        user.nationality
                            ? formatNationality(user.nationality, user.gender, "guess")
                            : undefined,
                        isWorkerProfile ? civilState : undefined,
                        user.workerProfile?.lastWorkPermit?.type
                            ? i18n.t(
                                  `work_permit_short.${user.workerProfile?.lastWorkPermit?.type}`,
                              )
                            : undefined,
                    ])}
                    onPress={isWorkerProfile ? editWorkerProfile : editClientProfile}
                />
                {isWorkerProfile && (
                    <ListItem
                        icon="civil-state"
                        description={buildDescription([
                            i18n.t("profile.avs_number"),
                            isWorkerProfile ? user.workerProfile?.avsNumber : undefined,
                        ])}
                        labels={
                            !user.workerProfile?.avsNumber &&
                            user.workerProfile?.swissOrHasPermitAnswer
                                ? [
                                      {
                                          text: i18n.t("profile.missing_avs_number"),
                                          mode: "warning",
                                      },
                                  ]
                                : []
                        }
                        // Navigation is possible only if the user does not have an AVS number
                        onPress={!user.workerProfile?.avsNumber ? editAvsNumber : undefined}
                    />
                )}
            </>
        );
    }, [
        civilState,
        editAvsNumber,
        editClientProfile,
        editWorkerProfile,
        formatNationality,
        i18n,
        intlFormat,
        isWorkerProfile,
        profilePicturePickerProps,
        updatingAvatar,
        user,
    ]);

    const accountInfo = useMemo(() => {
        if (!user) {
            return null;
        }
        return (
            <>
                <ListTitle value={i18n.t("profile.user_account")} />
                <ListItem
                    icon="email"
                    description={user.email || ""}
                    onPress={editAccount}
                    labels={
                        user.verified
                            ? []
                            : [
                                  {
                                      text: i18n.t("user.need_email_validation"),
                                      mode: "warning",
                                  },
                              ]
                    }
                />
                <ListItem
                    icon="phone"
                    description={user.phoneNumber || ""}
                    onPress={editPhoneNumber}
                />
                <ListItem
                    icon="activities"
                    onPress={toActivityLog}
                    description={i18n.t("profile.my_activity_log")}
                />
                <ListItem
                    onPress={openProfileSwitch}
                    rightIcon="right-left"
                    rightActionMode="medium"
                    rightIconColor={colors.secondary}
                    labels={[{ text: i18n.t("profile.change_profile"), mode: "cta" }]}
                />
                <LanguageSwitcher />
                <ListItem
                    labels={[{ text: i18n.t("profile.logout"), mode: "cta" }]}
                    rightIcon="arrow-right-from-bracket"
                    rightActionMode="medium"
                    rightIconColor={colors.secondary}
                    onPress={onLogout}
                />
                <Gap gap={2} />
                <ListItem
                    labels={[{ text: i18n.t("profile.deleteAccount"), mode: "redCta" }]}
                    rightActionMode="empty"
                    onPress={onDeleteAccount}
                />
            </>
        );
    }, [
        colors.secondary,
        editAccount,
        editPhoneNumber,
        i18n,
        onDeleteAccount,
        onLogout,
        openProfileSwitch,
        toActivityLog,
        user,
    ]);

    const ijmInfo = useMemo(() => {
        return (
            <ListItem
                labels={
                    !ijmGroup && canChangeIjmGroup
                        ? [
                              {
                                  text: i18n.t("ijm.profile.to_complete"),
                                  mode: "miniCta",
                              },
                          ]
                        : []
                }
                description={i18n.t("ijm.profile.title")}
                icon="ijm"
                onPress={() => navigation?.navigate("IJMConfigurationListScreen")}
            />
        );
    }, [canChangeIjmGroup, i18n, ijmGroup, navigation]);

    const versionInfo = useMemo(() => {
        return (
            <>
                {(Constants.expoConfig?.extra?.appEnv === "local" || devClickCount > 50) && (
                    <>
                        <Gap gap={2} />
                        <ListItem
                            labels={[{ text: `${i18n.t("profile.do_not_click")} 💣`, mode: "cta" }]}
                            rightActionMode="empty"
                            onPress={onThrowError}
                        />
                        <ListItem
                            labels={[{ text: "Storybook", mode: "cta" }]}
                            rightActionMode="empty"
                            onPress={openStorybook}
                        />
                        <ListItem
                            labels={[
                                { text: `${i18n.t("profile.sample_skribble")} 💸💸`, mode: "cta" },
                            ]}
                            rightActionMode="empty"
                            onPress={() => {
                                navigation.navigate("SkribbleSampleScreen");
                            }}
                        />
                    </>
                )}
                <Gap gap={1} />
                <AppVersion
                    onPress={() => {
                        setDevClickCount(devClickCount + 1);
                    }}
                />
                <Gap gap={1} />
            </>
        );
    }, [devClickCount, i18n, navigation, onThrowError]);

    const talentInfo = useMemo(() => {
        if (!user) {
            return null;
        }
        return (
            <>
                <ListItem
                    icon="dollar-square"
                    description={buildDescription([
                        i18n.t("screens.profile_editor.bank_account.title"),
                        `IBAN: ${user?.workerProfile?.bankAccount?.iban}`,
                    ])}
                    labels={
                        user?.workerProfile?.bankAccount?.iban
                            ? []
                            : [
                                  {
                                      text: i18n.t("profile.missing_bank_account"),
                                      mode: "warning",
                                  },
                              ]
                    }
                    onPress={editBankAccount}
                />
                <ListItem
                    icon="buildings"
                    description={i18n.t("profile.client_list")}
                    onPress={() => {
                        navigation?.navigate("ClientList");
                    }}
                />
                <ListItem
                    icon="training"
                    description={i18n.t("worker_profile.trainings_and_experiences")}
                    labels={
                        !user.workerProfile?.curriculumVitae
                            ? [
                                  {
                                      text: i18n.t("worker_profile.errors.education_level"),
                                      mode: "warning",
                                  },
                              ]
                            : undefined
                    }
                    onPress={editDiplomaAndTraining}
                />
                {user?.workerProfile?.documents?.map((document) => (
                    <ListItem
                        key={document.id}
                        icon="file"
                        description={document.title}
                        onPress={() => setDisplayedDocument(document)}
                    />
                ))}
            </>
        );
    }, [editBankAccount, editDiplomaAndTraining, i18n, navigation, user]);

    const frameworkContractLabel = useMemo<LabelProps | undefined>(() => {
        switch (client?.status) {
            case ClientStatus.Accepted:
                return { text: i18n.t("profile.awaiting"), mode: "warning" };
            case ClientStatus.Signed:
                return { text: i18n.t("profile.signed"), mode: "success" };
            case ClientStatus.Rejected:
                return { text: i18n.t("profile.rejected"), mode: "error" };
        }
    }, [client?.status, i18n]);

    switch (profileToUse) {
        case "Worker": //
            return (
                <>
                    {baseComponents}
                    <SimplePageContent refresh={refreshUser}>
                        {personalInfo}
                        {talentInfo}
                        {accountInfo}
                        {validProfilesNb > 1 && (
                            <ListItem
                                labels={[
                                    { text: i18n.t("profile.delete.worker.title"), mode: "redCta" },
                                ]}
                                rightActionMode="empty"
                                onPress={() => {
                                    void onDeleteWorkerProfile();
                                }}
                            />
                        )}
                        {versionInfo}
                    </SimplePageContent>
                </>
            );
        case "Client": //
            return (
                <>
                    {baseComponents}
                    <SimplePageContent refresh={refreshUser} headband={emailVerificationHeadband}>
                        <EmployerProfileWarnings />
                        <ListTitle value={i18n.t("client_profile.client_title")} />
                        <ListItem
                            {...companyPicturePickerProps}
                            useBigAvatarOrIcon={true}
                            avatarPath={client?.logoUrl}
                            isAvatarLoading={updatingLogo}
                            labels={[
                                { text: i18n.t("client_profile.change_client_logo"), mode: "cta" },
                            ]}
                            rightActionMode="empty"
                        />
                        <ListItem
                            description={buildDescription([
                                client?.name,
                                client?.address?.formatted,
                            ])}
                            onPress={editClient}
                            icon="buildings"
                        />
                        <ListItem
                            icon="email"
                            description={currentProfile?.businessEmail || ""}
                            onPress={editBusinessEmail}
                            labels={[
                                currentProfile?.businessEmailVerified
                                    ? {
                                          text: i18n.t("user.business_email_info"),
                                          mode: "info",
                                      }
                                    : {
                                          text: i18n.t("user.business_email_need_validation"),
                                          mode: "warning",
                                      },
                            ]}
                        />
                        <ListItem
                            icon="address-card"
                            onPress={editClientContact}
                            description={buildDescription([
                                i18n.t("client_profile.contact_info"),
                                client?.contactEmail,
                                client?.contactPhoneNumber,
                            ])}
                        />
                        <ListTitle value={i18n.t("screens.profile_editor.administrative_data")} />
                        {ijmInfo}
                        {client?.id && client.contractEffectiveAsset && frameworkContractLabel && (
                            <ListItem
                                icon="file-pen"
                                onPress={toFrameworkContract}
                                description={i18n.t("profile.framework_contract")}
                                badge={<Label {...frameworkContractLabel} />}
                            />
                        )}
                        <ListItem
                            icon="colleagues"
                            onPress={toColleagues}
                            description={i18n.t("profile.my_colleagues")}
                        />
                        {personalInfo}
                        {accountInfo}
                        {validProfilesNb > 1 && (
                            <ListItem
                                labels={[
                                    { text: i18n.t("profile.delete.client.title"), mode: "redCta" },
                                ]}
                                rightActionMode="empty"
                                onPress={() => {
                                    void onDeleteClientProfile();
                                }}
                            />
                        )}
                        {versionInfo}
                    </SimplePageContent>
                </>
            );
        case "PrivateClient": //
            return (
                <>
                    {baseComponents}
                    <SimplePageContent refresh={refreshUser} headband={emailVerificationHeadband}>
                        <EmployerProfileWarnings />
                        {personalInfo}
                        <ListTitle value={i18n.t("screens.profile_editor.administrative_data")} />
                        {ijmInfo}
                        {client?.id && client.contractEffectiveAsset && frameworkContractLabel && (
                            <ListItem
                                icon="file-pen"
                                onPress={toFrameworkContract}
                                description={i18n.t("profile.framework_contract")}
                                badge={<Label {...frameworkContractLabel} />}
                            />
                        )}
                        {accountInfo}
                        {validProfilesNb > 1 && (
                            <ListItem
                                labels={[
                                    {
                                        text: i18n.t("profile.delete.private_client.title"),
                                        mode: "redCta",
                                    },
                                ]}
                                rightActionMode="empty"
                                onPress={() => {
                                    void onDeletePrivateClientProfile();
                                }}
                            />
                        )}
                        {versionInfo}
                    </SimplePageContent>
                </>
            );
        default:
            return (
                <VStack>
                    <Loading />
                </VStack>
            );
    }
};

export default ProfileScreen;
